import * as React from 'react';
import * as uuidv4 from 'uuid/v4';
import { ComposerBridge, ClipboardInfo } from 'ark-common';
import Composer from './components/Composer';
import { EntireComposerState, ComposerPictureStorage } from './reducers/composer';
import * as ComposerAction from './actions/ComposerAction';
import {
  collectPictureIDsFromJSONReadyDrawingData,
  DrawingState,
  dejsonizeDrawingData,
} from '../drawing/reducers/drawing';
import { isBuiltinBackground, PagePicture } from './types';
import createBlobFromCanvas from '../drawing/util/createBlobFromCanvas';
import createCanvasFromBlob from '../drawing/util/createCanvasFromBlob';
import PictureStorageAction from '../drawing/actions/PictureStorageAction';
import { initPictureStorageItems, initPictureStorage } from '../drawing/reducers/pictureStorage';
import PictureStorageWorker from '../drawing/helpers/PictureStorageWorker';
import { connect } from 'react-redux';
import { Dispatch, Action } from 'redux';
import { AppStates } from '../reducers/app';
import { JSONReadyClipbookData, isReadyToSaveClipbook } from './reducers/clipbook';

interface ComposerContainerOwnProps {
  readonly bridge: ComposerBridge;
}

interface ComposerContainerProps extends ComposerContainerOwnProps {
  readonly composer: EntireComposerState;
  readonly dispatch: Dispatch<Action>;
}

class ComposerContainer extends React.Component<ComposerContainerProps> {
  private pagePictureStorageWorker = new PictureStorageWorker<PagePicture>({
    dispatch: results => this.props.dispatch(
      PictureStorageAction.finishTasks(ComposerPictureStorage.Pages, results),
    ),
    read: id => this.props.bridge.loadClipbookAttachment(this.props.composer.clipbook!.id, id),
    write: (id, blob) => {
      return this.props.bridge.saveClipbookAttachment(this.props.composer.clipbook!.id, id, blob);
    },
    encode: async image => image.blob,
    decode: async blob => ({ blob, url: URL.createObjectURL(blob) }),
  });

  private drawingPictureStorageWorker = new PictureStorageWorker<HTMLCanvasElement>({
    dispatch: results => this.props.dispatch(
      PictureStorageAction.finishTasks(ComposerPictureStorage.Drawing, results),
    ),
    read: id => this.props.bridge.loadClipbookAttachment(this.props.composer.clipbook!.id, id),
    write: (id, blob) => {
      return this.props.bridge.saveClipbookAttachment(this.props.composer.clipbook!.id, id, blob);
    },
    encode: image => createBlobFromCanvas(image),
    decode: blob => createCanvasFromBlob(document, blob),
  });

  private async loadClipbook(id: string) {
    const main = await this.props.bridge.loadClipbookMain(id) as JSONReadyClipbookData;
    const clipbook = {
      id,
      name: main.name,
      pages: main.pages,
      currentPage: main.currentPage,
    };

    const pagePictureIDs: { [id: string]: true } = {};
    for (const page of clipbook.pages) {
      if (isBuiltinBackground(page.backgroundID)) {
        continue;
      }

      pagePictureIDs[page.backgroundID] = true;
    }

    let pagePictures = initPictureStorageItems<PagePicture>(
      initPictureStorage(),
      pictureID => (
        clipbook.currentPage !== null
        && pictureID === clipbook.pages[clipbook.currentPage].backgroundID
      ),
      Object.keys(pagePictureIDs),
    );

    if (this.props.composer.clipboard !== null) {
      const { pictureID } = this.props.composer.clipboard;
      pagePictures = {
        ...pagePictures,
        items: {
          ...pagePictures.items,
          [pictureID]: this.props.composer.pagePictures.items[pictureID],
        },
      };
    }

    const drawings: { [id: string]: DrawingState } = {};
    const drawingPictureIDs: { [id: string]: true } = {};
    const demandedDrawingImages: { [id: string]: true } = {};
    for (const pageID in main.drawings) {
      const srcDrawing = main.drawings[pageID];
      drawings[pageID] = dejsonizeDrawingData(srcDrawing);

      const references = collectPictureIDsFromJSONReadyDrawingData(srcDrawing);
      if (
        clipbook.currentPage !== null
        && pageID === clipbook.pages[clipbook.currentPage].uniqueID
      ) {
        for (const pictureID of references) {
          demandedDrawingImages[pictureID] = true;
        }
      }

      for (const pictureID of references) {
        drawingPictureIDs[pictureID] = true;
      }
    }

    return ComposerAction.finishLoadingClipbook(
      clipbook,
      pagePictures,
      drawings,
      initPictureStorageItems(
        initPictureStorage(),
        id => id in demandedDrawingImages,
        Object.keys(drawingPictureIDs),
      ),
    );
  }

  private async saveClipbook(state: EntireComposerState) {
    const { savingClipbook } = state;
    if (savingClipbook === null) {
      throw new Error('Illegal state.');
    }

    const attachments: { [id: string]: true } = {};

    const { drawings } = savingClipbook.body;
    for (const pageID in drawings) {
      for (const id of collectPictureIDsFromJSONReadyDrawingData(drawings[pageID])) {
        attachments[id] = true;
      }
    }

    for (const page of savingClipbook.body.pages) {
      if (!isBuiltinBackground(page.backgroundID)) {
        attachments[page.backgroundID] = true;
      }
    }

    await this.props.bridge.saveClipbook(
      savingClipbook.id,
      savingClipbook.body,
      Object.keys(attachments),
    );
  }

  private finishSavingProcess() {
    this.props.dispatch(ComposerAction.finishSavingClipbook());
  }

  private startSavingProcessIfNeeded(prevState: EntireComposerState) {
    if (
      isReadyToSaveClipbook(
        this.props.composer.savingClipbook,
        this.props.composer.pagePictures,
        this.props.composer.drawingPictures,
      )
      && (
        prevState.savingClipbook !== this.props.composer.savingClipbook
        || (
          !isReadyToSaveClipbook(
            prevState.savingClipbook,
            prevState.pagePictures,
            prevState.drawingPictures,
          )
        )
      )
    ) {
      this.saveClipbook(this.props.composer).then(() => {
        this.finishSavingProcess();
      }).catch(() => {
        alert('バインダーの保存中にエラーが発生しました。');
        this.finishSavingProcess();
      });
    }
  }

  private prunePictureURLs(prevState: EntireComposerState) {
    if (this.props.composer.pagePictures === prevState.pagePictures) {
      return;
    }

    const urls: { [url: string]: true } = {};

    for (const pictureID in prevState.pagePictures.items) {
      const { image } = prevState.pagePictures.items[pictureID];
      if (image !== null) {
        urls[image.url] = true;
      }
    }

    for (const pictureID in this.props.composer.pagePictures.items) {
      const { image } = this.props.composer.pagePictures.items[pictureID];
      if (image !== null) {
        delete urls[image.url];
      }
    }

    for (const url in urls) {
      URL.revokeObjectURL(url);
    }
  }

  private handleStateChange(prevState: EntireComposerState) {
    this.startSavingProcessIfNeeded(prevState);
    this.prunePictureURLs(prevState);

    this.pagePictureStorageWorker.update(
      prevState.pagePictures,
      this.props.composer.pagePictures,
    );
    this.drawingPictureStorageWorker.update(
      prevState.drawingPictures,
      this.props.composer.drawingPictures,
    );
  }

  private handleOpenClipbook = (id: string) => {
    this.props.dispatch(ComposerAction.startLoadingClipbook());

    this.loadClipbook(id).then((action) => {
      this.props.dispatch(action);
    }).catch(() => {
      alert('バインダーの読み込み中にエラーが発生しました。');
      this.props.dispatch(ComposerAction.abortLoadingClipbook());
    });
  }

  public constructor(props: ComposerContainerProps) {
    super(props);

    props.bridge.existingClipbooks().then((index) => {
      this.props.dispatch(ComposerAction.loadClipbookIndex(index));
    }).catch(() => alert('バインダー一覧の読み込み中にエラーが発生しました。'));
  }

  public render() {
    return <Composer onOpenClipbook={this.handleOpenClipbook} />;
  }

  public componentDidUpdate(prevProps: ComposerContainerProps) {
    this.handleStateChange(prevProps.composer);
  }

  public createNewPageFromClipboard = async (info: ClipboardInfo) => {
    try {
      const blob = await this.props.bridge.blobFromClipboard();
      const url = URL.createObjectURL(blob);
      this.props.dispatch(ComposerAction.receiveNewPageFromClipboard(uuidv4(), blob, url, info));
    } catch (e) {
      alert('エラーが発生しました。');
    }
  }
}

function mapStateToProps(state: AppStates) {
  return {
    composer: state.composer,
  };
}

export default connect(mapStateToProps)(ComposerContainer);
